import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { coordinatorView } from "@/data/showcase";
import { fadeUp, viewportOnce } from "@/lib/motion";

const { eyebrow, titleStart, titleItalic, modules, examples } = coordinatorView;

export function CoordinatorView() {
  return (
    <section
      id="workflow"
      className="relative bg-[#06080F] border-t border-ink-900 py-16 md:py-24 overflow-hidden"
    >
      <div className="relative mx-auto max-w-[1080px] px-6">
        <motion.div
          className="mb-12 md:mb-16 max-w-[760px]"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-3">
            {eyebrow}
          </p>
          <h2 className="font-serif text-white leading-[1.05] text-3xl md:text-4xl lg:text-5xl max-w-[20ch]">
            {titleStart}{" "}
            <span className="text-foton-lighter italic font-display">{titleItalic}</span>
          </h2>
        </motion.div>

        {/* Editorial narrative + single example card. No premium gradient frame,
            no module grid. The card itself is the proof, narrative gives context. */}
        <div className="grid md:grid-cols-[1fr_1fr] gap-10 md:gap-12 lg:gap-16 items-start">
          {/* Left: notebook-style morning narrative */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="order-2 md:order-1"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-foton-lighter mb-5">
              · poranek koordynatora ·
            </p>
            <dl className="space-y-3 text-sm md:text-base text-ink-300">
              <MorningEntry time="08:48" text="otwiera Foton" detail="cztery propozycje w skrzynce" />
              <MorningEntry time="08:50" text="zatwierdza gładź salonu" detail="+8% postępu, bez poprawek" highlight />
              <MorningEntry time="08:52" text="zatwierdza płytki łazienka" detail="+12% postępu" highlight />
              <MorningEntry time="08:54" text="zamawia fugę u Cersanit" detail="faktura KSeF, dostawa jutro" />
              <MorningEntry time="08:56" text="dzwoni do elektryka" detail="kamera w garażu offline od dwóch godzin" />
              <MorningEntry time="09:00" text="wraca do roboty" detail="" />
            </dl>

            {/* Module list as mono editorial annotation, NOT a card grid */}
            <div className="mt-12 pt-6 border-t border-ink-800">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-4">
                W panelu obok analizy postępu
              </p>
              <ul className="font-mono text-xs text-ink-400 leading-loose columns-2 gap-x-6">
                {modules.map((m) => (
                  <li key={m.name} className="break-inside-avoid">
                    <span className="text-ink-200">{m.name}</span>
                    <span className="text-ink-600"> · {m.sub}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Right: single AI card example, cycling. Simple chrome, no gradient frame. */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="order-1 md:order-2 md:sticky md:top-12"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-5 md:text-right">
              propozycja AI · 1 z 4
            </p>
            <AiCarousel />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function MorningEntry({
  time,
  text,
  detail,
  highlight = false,
}: {
  time: string;
  text: string;
  detail: string;
  highlight?: boolean;
}) {
  return (
    <div className="grid grid-cols-[56px_1fr] gap-4 items-baseline border-b border-ink-800 last:border-b-0 pb-3">
      <span className={"font-mono text-[11px] tabular-nums " + (highlight ? "text-foton-lighter" : "text-ink-500")}>
        {time}
      </span>
      <div>
        <p className={"font-serif text-base md:text-lg " + (highlight ? "text-white" : "text-ink-200")}>
          {text}
        </p>
        {detail && (
          <p className="font-mono text-[11px] text-ink-500 mt-0.5">{detail}</p>
        )}
      </div>
    </div>
  );
}

function AiCarousel() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % examples.length), 4500);
    return () => clearInterval(id);
  }, []);
  const current = examples[index];
  return (
    <div className="relative">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="border border-ink-700 bg-ink-900 p-5 relative"
        >
          {/* Top hairline accent in foton, the ONLY foton tint here */}
          <span
            aria-hidden
            className="absolute top-0 left-5 right-5 h-px"
            style={{
              background:
                "linear-gradient(to right, transparent, #BB97FF 50%, transparent)",
            }}
          />
          <AiCard example={current} />
        </motion.div>
      </AnimatePresence>
      <div className="flex justify-center md:justify-end gap-1 mt-4">
        {examples.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Pokaż przykład ${i + 1}`}
            className="group p-2 -m-2"
          >
            <span
              className={
                "block h-px transition-all " +
                (i === index ? "w-10 bg-foton-light" : "w-4 bg-ink-700 group-hover:bg-ink-500")
              }
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function AiCard({ example }: { example: typeof examples[0] }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="font-serif text-xl text-white leading-tight min-w-0">
          {example.headline}
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foton-lighter shrink-0">
          ✦ AI
        </span>
      </div>
      <p className="font-mono text-[11px] text-ink-500 mb-5 truncate">{example.meta}</p>

      <div className="grid grid-cols-2 gap-2 mb-5">
        <img
          src={example.before}
          alt=""
          loading="lazy"
          className="w-full aspect-[4/3] object-cover"
        />
        <img
          src={example.after}
          alt=""
          loading="lazy"
          className="w-full aspect-[4/3] object-cover"
        />
      </div>

      <div className="flex items-baseline justify-between border-t border-ink-800 pt-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500">
          {example.range}
        </span>
        <span className="font-serif text-3xl text-foton-light num leading-none">
          {example.delta}
        </span>
      </div>

      <div className="flex gap-2 mt-5">
        <button className="bg-foton text-white px-4 py-2.5 text-xs font-medium uppercase tracking-wider flex-1 hover:bg-foton-dark transition-colors">
          Zatwierdź
        </button>
        <button className="border border-ink-700 text-ink-200 px-4 py-2.5 text-xs font-medium uppercase tracking-wider hover:border-ink-500 transition-colors">
          Popraw
        </button>
      </div>
    </div>
  );
}
